/*
 * Просьба о согласии.
 *
 * Намеренно не окно и не затемнение. Кино идёт, маршрут листается, ничего не
 * блокируется — человек вправе не отвечать вовсе, и сайт от этого не меняется
 * ни на пиксель. Это и есть разница между просьбой и вымогательством согласия.
 *
 * Разметка живёт в index.html, а не собирается здесь: так её переводит общий
 * каталог вместе со всей остальной копией сайта, и текст просьбы проходит ту
 * же сверку, что и любая другая строка.
 */

type View = Window & typeof globalThis;

export interface ConsentBadgePorts {
  view: View;
  onChoice(choice: "granted" | "denied"): void;
}

export interface ConsentBadge {
  open(): void;
  close(): void;
  readonly visible: boolean;
}

const LEAVE_MS = 260;

export function createConsentBadge(ports: ConsentBadgePorts): ConsentBadge | null {
  const { view, onChoice } = ports;
  const document = view.document;
  const root = document.querySelector<HTMLElement>("[data-bb-consent]");
  if (!root) return null;

  const accept = root.querySelector<HTMLButtonElement>("[data-bb-consent-accept]");
  const decline = root.querySelector<HTMLButtonElement>("[data-bb-consent-decline]");
  let visible = false;
  let closing = 0;

  /*
   * Стрелка маршрута стоит внизу по центру. Пока просьба на экране, она
   * поднимается ровно на её высоту: два элемента в одном стеке, а не один
   * поверх другого.
   */
  const lift = (): void => {
    const height = visible ? Math.round(root.getBoundingClientRect().height) + 12 : 0;
    document.body.style.setProperty("--bb-consent-lift", `${height}px`);
  };

  const close = (): void => {
    if (!visible) return;
    visible = false;
    root.dataset.state = "leaving";
    document.body.removeAttribute("data-consent-open");
    lift();
    view.clearTimeout(closing);
    closing = view.setTimeout(() => {
      root.hidden = true;
      root.removeAttribute("data-state");
    }, LEAVE_MS);
  };

  const choose = (choice: "granted" | "denied") => (): void => {
    close();
    onChoice(choice);
  };

  accept?.addEventListener("click", choose("granted"));
  decline?.addEventListener("click", choose("denied"));

  return {
    open() {
      if (visible) return;
      view.clearTimeout(closing);
      visible = true;
      root.hidden = false;
      root.dataset.state = "entering";
      document.body.setAttribute("data-consent-open", "");
      // Два кадра: первый монтирует, второй снимает начальное смещение, иначе
      // переход не запускается вовсе и просьба просто возникает рывком.
      view.requestAnimationFrame(() => view.requestAnimationFrame(() => {
        root.removeAttribute("data-state");
        lift();
      }));
    },
    close,
    get visible() { return visible; },
  };
}
